import { createStyles, Group, Stack, Textarea } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { useDebounceFn } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { RichEditor } from '../../RichEditor';
import { ENTITY_TYPES_MODAL_CONFIG } from '../constants';
import { entityModalStore } from '../store';

const useStyles = createStyles((theme) => ({
	center: {
		backgroundColor: theme.other.getColor('fill/primary/active'),
	},
	title: {
		padding: 0,

		'.mantine-Textarea-input': {
			color: theme.other.getColor('text/primary/default'),
			fontSize: theme.fontSizes.xl,
			fontWeight: theme.other.typography.weight.semibold,
			lineHeight: 1.3,
			minHeight: 28,
			padding: `0 ${theme.spacing.xs}`,
			overflowY: 'hidden',
			resize: 'none',
			wordWrap: 'break-word',
			whiteSpace: 'pre-wrap',
			'&:focus': {
				outline: 'none',
				borderColor: 'transparent',
				boxShadow: 'none',
			},
		},
	},
	content: {
		padding: `0 ${theme.spacing.xs} ${theme.spacing.md} ${theme.spacing.xs}`,
	},
}));

function EntityModalHeader() {
	const { classes } = useStyles();

	const findRelatedQuestions = useDebounceFn(
		entityModalStore.findRelatedQuestions,
		{ wait: 300 }
	);

	const handleTitleChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		entityModalStore.setTitle(event.currentTarget.value);
		findRelatedQuestions.run();
	};

	const handleDescriptionChange = (description?: string) => {
		entityModalStore.setDescription(description ?? '');
	};

	const config =
		entityModalStore.type && ENTITY_TYPES_MODAL_CONFIG[entityModalStore.type];

	return (
		// We put the `key` prop here to force re-rendering of the component on
		// template change.
		<Stack key={entityModalStore.template?.id} spacing="md">
			<Stack spacing="xs">
				<Textarea
					className={classes.title}
					placeholder="Untitled"
					size="xl"
					variant="unstyled"
					autosize
					// Do not use `value={entityModalStore.title}` directly here
					// because it's an observed value and will cause re-renders.
					defaultValue={
						entityModalStore.template?.title ?? entityModalStore.title ?? ''
					}
					onChange={handleTitleChange}
					data-autofocus
				/>
				{entityModalStore.titleValidationError && (
					<Group spacing="2xs">
						<Icon name="alertCircle" color="icon/critical/default" />
						<Text size="sm" color="text/critical/default">
							{entityModalStore.titleValidationError}
						</Text>
					</Group>
				)}
			</Stack>
			<RichEditor
				className={classes.content}
				readOnly={false}
				disableTopGap
				// Do not use `value={entityModalStore.description}` directly here
				// because it's an observed value and will cause re-renders.
				initialValue={entityModalStore.description}
				// We need to force re-rendering of the editor when the template changes
				// to update the initial value.
				key={String(entityModalStore.metadata['readyId'] ?? 'no-template')}
				emptyDocPlaceholder={config?.richEditorPlaceholder}
				onChangeCallback={handleDescriptionChange}
				{...(config?.richEditorProps ?? {})}
			/>
		</Stack>
	);
}

const EntityModalHeaderObserver = observer(EntityModalHeader);
export default EntityModalHeaderObserver;
